import Button from '@pinecast/common/Button';
import Callout from '@pinecast/common/Callout';
import Checkbox from '@pinecast/common/Checkbox';
import {toMediumDate} from '@pinecast/common/dates';
import EmptyState from '@pinecast/common/EmptyState';
import ErrorState from '@pinecast/common/ErrorState';
import LoadingState from '@pinecast/common/LoadingState';
import MarkdownBody from '@pinecast/common/MarkdownBody';
import Section from '@pinecast/common/Section';
import {useManagedMutation, useQuery} from '@pinecast/gql';
import type {RecurringDonation_tipJarSubscription} from '@pinecast/gql/__generated_tj__/RecurringDonation';
import type {
  SetUpdatesOptIn,
  SetUpdatesOptInVariables,
} from '@pinecast/gql/__generated_tj__/SetUpdatesOptIn';
import type {
  TipJarUpdates,
  TipJarUpdates_tipJarUpdates_items,
  TipJarUpdatesVariables,
} from '@pinecast/gql/__generated_tj__/TipJarUpdates';
import {FormattedMessage} from '@pinecast/i18n';
import {useColors} from '@pinecast/styles';
import gql from 'graphql-tag';
import * as React from 'react';

const PAGE_SIZE = 10;

const TIP_JAR_UPDATES = gql`
  query TipJarUpdates($slug: ID!, $offset: Int, $limit: Int) {
    tipJarUpdates(podcastSlug: $slug, offset: $offset, limit: $limit) {
      items {
        id
        title
        body
        publishDate
      }
      total
    }
  }
`;

const SET_UPDATES_OPT_IN = gql`
  mutation SetUpdatesOptIn($slug: ID!, $optedIn: Boolean!) {
    setUpdatesOptIn(podcastSlug: $slug, optedIn: $optedIn) {
      optedIn
    }
  }
`;

const UpdateItem = ({update}: {update: TipJarUpdates_tipJarUpdates_items}) => {
  const designColors = useColors();
  return (
    <Section>
      <article>
        <h2
          style={{
            fontSize: 18,
            fontWeight: 500,
            marginBottom: 4,
            marginTop: 0,
          }}
        >
          {update.title}
        </h2>
        <div
          style={{
            color: designColors.contentTextAccent,
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          {toMediumDate(update.publishDate)}
        </div>
        <MarkdownBody markdown={update.body} />
      </article>
    </Section>
  );
};

const RecurringDonationPageUpdates = ({
  subscription,
}: {
  subscription: RecurringDonation_tipJarSubscription;
}) => {
  const slug = subscription.podcast.slug;

  const [receivesUpdates, setReceivesUpdates] = React.useState(
    subscription.receivesUpdates,
  );
  const [shownCount, setShownCount] = React.useState(PAGE_SIZE);

  const updates = useQuery<TipJarUpdates, TipJarUpdatesVariables>(
    TIP_JAR_UPDATES,
    {variables: {slug, offset: 0, limit: shownCount}},
  );

  const setOptIn = useManagedMutation<
    SetUpdatesOptIn,
    SetUpdatesOptInVariables
  >(SET_UPDATES_OPT_IN);

  const handleToggle = React.useCallback(
    async (checked: boolean) => {
      setReceivesUpdates(checked);
      const result = await setOptIn.action({
        variables: {slug, optedIn: checked},
      });
      if (!result) {
        // The mutation failed; reflect the server's state again.
        setReceivesUpdates(!checked);
      }
    },
    [setOptIn, slug],
  );

  return (
    <>
      <Section>
        <Checkbox
          checked={receivesUpdates}
          disabled={setOptIn.pending}
          onChange={handleToggle}
          text={
            <FormattedMessage
              description="Checkbox to opt in to email updates from a podcast the listener supports"
              defaultMessage="Email me new updates from this podcast"
            />
          }
        />
        {setOptIn.error && (
          <Callout type="negative" style={{marginTop: 12}}>
            <FormattedMessage
              description="Error shown when the updates email preference could not be saved"
              defaultMessage="We weren't able to save your preference. Try again later."
            />
          </Callout>
        )}
      </Section>
      {updates.loading ? (
        <Section>
          <LoadingState
            title={
              <FormattedMessage
                description="Loading message for the subscriber updates list"
                defaultMessage="Loading updates…"
              />
            }
          />
        </Section>
      ) : updates.error || !updates.data.tipJarUpdates ? (
        <Section>
          <ErrorState
            title={
              <FormattedMessage
                description="Error message for the subscriber updates list"
                defaultMessage="Could not load updates."
              />
            }
          />
        </Section>
      ) : updates.data.tipJarUpdates.items.length === 0 ? (
        <Section>
          <EmptyState
            title={
              <FormattedMessage
                description="Message shown when a podcast has not sent any subscriber updates"
                defaultMessage="No updates yet"
              />
            }
            copy={
              <FormattedMessage
                description="Copy shown when a podcast has not sent any subscriber updates"
                defaultMessage="When this podcast shares an update with its supporters, it will appear here."
              />
            }
          />
        </Section>
      ) : (
        <>
          {updates.data.tipJarUpdates.items.map(update => (
            <UpdateItem key={update.id} update={update} />
          ))}
          {updates.data.tipJarUpdates.items.length <
            updates.data.tipJarUpdates.total && (
            <Section>
              <Button
                onClick={() => setShownCount(count => count + PAGE_SIZE)}
                style={{margin: '0 auto'}}
              >
                <FormattedMessage
                  description="Button to load older subscriber updates"
                  defaultMessage="Load more"
                />
              </Button>
            </Section>
          )}
        </>
      )}
    </>
  );
};

export default RecurringDonationPageUpdates;
